import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Grid, LinearProgress } from '@mui/material';

import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { TestAttributeValueDTO, TestAttributeValueFilterDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField } from '../../../shared/components';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { AttributeMaintenance } from '../../appSphere/attribute/AttributeMaintenance';
import { TestComponentDTO } from '../../../shared/interfaces/testingSphere/TestComponentDTO';
import { TestComponentFilterDTO } from '../../../shared/interfaces/testingSphere/TestComponentFilterDTO';

interface ITestAttributeValueProps {
    data: any;
}

export const TestAttributeValue: React.FC<ITestAttributeValueProps> = ({ data }) => {
    const [testComponentList, setTestComponentList] = useState<TestComponentDTO[]>([]);
    const [testAttributeValueList, setTestAttributeValueList] = useState<TestAttributeValueDTO[]>([]);
    const [testAttributeValueFilterDTO, setTestAttributeValueFilterDTO] = useState<TestAttributeValueFilterDTO>();

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [expadedData, setExpandedData] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    useEffect(() => {
        setTestAttributeValueFilterDTO({
            ...testAttributeValueFilterDTO,
            name: '', testComponentId: data.id ?? ''
        });

        handleTestComponentGetAllAsync();
    }, []);

    useEffect(() => {
        handleTestAttributeValueGetAllViewAsync();
    }, [testAttributeValueFilterDTO?.testComponentId]);

    useEffect(() => {
        if (actionExecuted)
            handleTestAttributeValueGetAllViewAsync();
    }, [actionExecuted]);

    const handleTestComponentGetAllAsync = useCallback(async () => {
        const testComponentFilterDTO: TestComponentFilterDTO = {
            name: '',
            testScenarioId: '',
        };
        GenericService.GetAllViewAsync1('testingsphere.testcomponent', testComponentFilterDTO, setActionExecuted, setLoading, setTestComponentList, showMessage);
    }, []);

    const handleTestAttributeValueGetAllViewAsync = useCallback(async () => {
        if (testAttributeValueFilterDTO === undefined)
            return;
        GenericService.GetAllViewAsync1('testingsphere.testAttributeValue', testAttributeValueFilterDTO, setActionExecuted, setLoading, setTestAttributeValueList, showMessage);
    }, [testAttributeValueFilterDTO]);

    const handleClearFilterClick = () => {
        setTestAttributeValueFilterDTO({ ...testAttributeValueFilterDTO, name: '' } as TestAttributeValueFilterDTO);
    };

    function handleSearchClick() {
        handleTestAttributeValueGetAllViewAsync();
    }

    const handleNew = () => {
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        { id: 'new', text: 'New Attribute Value', icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: 'View Details', icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: 'Delete', icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
    ];

    const chipFilters: IChipFilter[] = [
        { label: 'Total Records', value: testAttributeValueList.length },
        { label: 'Name', value: testAttributeValueFilterDTO?.name },
        { label: 'TestComponent', value: !data ? testComponentList.find(x => x.id === testAttributeValueFilterDTO?.testComponentId)?.name : undefined },
    ];

    const columns: ICardColumns[] = [
        { field: 'attribute', label: 'Name', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'action', label: 'Action', size: 2, type: 'text', visible: true, isChip: true, hasFieldLine: false, hasToolTip: false },
        { field: 'value', label: 'Value', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'automationId', label: 'Automation Id', size: 3, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'identificationType', label: 'Identification Type', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'orderExecution', label: 'Order Execution', size: 1, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title='APPLICATION'>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={4}>
                            <TextFieldInput
                                id='name'
                                label='Name'
                                value={testAttributeValueFilterDTO?.name}
                                setValue={setTestAttributeValueFilterDTO}
                                obj={testAttributeValueFilterDTO}
                                placeholder={'Name'}
                                maxLength={200}
                            />
                        </Grid>
                        {!data && (
                            <Grid item xs={12} md={3}>
                                <SelectField
                                    id='testComponentId'
                                    label='TestComponent Name'
                                    value={testAttributeValueFilterDTO?.testComponentId}
                                    setValue={setTestAttributeValueFilterDTO}
                                    obj={testAttributeValueFilterDTO}
                                    valueList={testComponentList}
                                />
                            </Grid>
                        )}
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <CardList
                    columns={columns}
                    rows={testAttributeValueList}
                    actions={cardActions}
                    hasChildren={false}
                    setRowExpanded={setExpandedData}
                    cardSize={12}
                />
                {newRecord && (
                    <AttributeMaintenance
                        data={''}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                        parentId={data.id ?? testAttributeValueFilterDTO?.testComponentId}
                        isAttributeValue={true}
                    />
                )}
                {editRecord && (
                    <AttributeMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                        isAttributeValue={true}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'testingsphere.testattributevalue'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                    indexAttributeLabel={2}
                />)}
            </LayoutDashboard>
        </React.Fragment >
    );
};